import {
    DEFAULT_CENSOR_MASK,
    GENDERS,
    SCHUFA_DECISION,
    SCHUFA_RATE_TYPES,
    SCHUFA_STATE,
    SCHUFA_STATUS,
} from 'cpool-constants';
import { schemaId, schemaMappedSchufaResponse } from 'cpool-schemas';
import { z } from 'zod';

import type { EventSchema } from '../../../utils/EventSchema';
import { _Event } from '../../_Event';

export class LoanRequest_ApplicantCreditRatingReceived extends _Event {
    constructor(payload: EventSchema<LoanRequest_ApplicantCreditRatingReceived>, deprecated = false) {
        super(payload, deprecated);
    }

    deprecated = true;

    getSchema() {
        return schemaMappedSchufaResponse.merge(z.object({ applicantId: schemaId }));
    }

    getCensoredSchema() {
        return this.getSchema().transform((data) => {
            /* eslint-disable no-param-reassign */
            if (data.identifier) {
                data.identifier = DEFAULT_CENSOR_MASK;
            }
            if (data.consorsResponse?.identifier) {
                data.consorsResponse.identifier = DEFAULT_CENSOR_MASK;
            }
            // Censor top-level applicantData
            if (data.applicantData?.birthPlace) {
                data.applicantData.birthPlace = DEFAULT_CENSOR_MASK;
            }
            if (data.applicantData?.firstName) {
                data.applicantData.firstName = DEFAULT_CENSOR_MASK;
            }
            if (data.applicantData?.lastName) {
                data.applicantData.lastName = DEFAULT_CENSOR_MASK;
            }
            if (data.applicantData?.currentAddress?.street) {
                data.applicantData.currentAddress.street = DEFAULT_CENSOR_MASK;
            }
            if (data.applicantData?.previousAddress?.street) {
                data.applicantData.previousAddress.street = DEFAULT_CENSOR_MASK;
            }

            // Censor consorsResponse.schufaInfo.schufaKopf fields
            if (data.consorsResponse?.schufaInfo?.schufaKopf?.birthPlace) {
                data.consorsResponse.schufaInfo.schufaKopf.birthPlace = DEFAULT_CENSOR_MASK;
            }
            if (data.consorsResponse?.schufaInfo?.schufaKopf?.firstName) {
                data.consorsResponse.schufaInfo.schufaKopf.firstName = DEFAULT_CENSOR_MASK;
            }
            if (data.consorsResponse?.schufaInfo?.schufaKopf?.lastName) {
                data.consorsResponse.schufaInfo.schufaKopf.lastName = DEFAULT_CENSOR_MASK;
            }
            if (data.consorsResponse?.schufaInfo?.schufaKopf?.currentAddress?.street) {
                data.consorsResponse.schufaInfo.schufaKopf.currentAddress.street = DEFAULT_CENSOR_MASK;
            }
            if (data.consorsResponse?.schufaInfo?.schufaKopf?.previousAddress?.street) {
                data.consorsResponse.schufaInfo.schufaKopf.previousAddress.street = DEFAULT_CENSOR_MASK;
            }
            /* eslint-enable no-param-reassign */

            return data;
        });
    }

    static example(): EventSchema<LoanRequest_ApplicantCreditRatingReceived> {
        return {
            applicantData: {
                birthDate: '2020-09-01',
                birthPlace: 'Berlin',
                currentAddress: {
                    city: 'Berlin',
                    postalCode: '12345',
                    street: 'Some street',
                },
                firstName: 'John',
                gender: GENDERS.MALE,
                lastName: 'Doe',
                previousAddress: {
                    city: 'München',
                    postalCode: '80331',
                    street: 'Müllerstraße 1',
                },
            },
            applicantId: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
            consorsResponse: {
                identifier: '1234567890',
            },
            decision: SCHUFA_DECISION.YES,
            duplicatedRequest: false,
            error: 'some_error',
            identifier: 'some_identifier',
            positions: [
                {
                    amount: 100,
                    code: 'some_code',
                    date: '2020-09-01',
                    internalLoan: false,
                    period: 200,
                    rate: 500,
                    rateType: SCHUFA_RATE_TYPES.MONTHLY,
                    remainingAmount: 2000,
                    reportingAmount: 1500,
                    type: 'ratenkredit',
                },
            ],
            score: 1,
            state: SCHUFA_STATE.OK,
            status: SCHUFA_STATUS.COMPLETED,
        };
    }

    static exampleCensored(): z.infer<ReturnType<LoanRequest_ApplicantCreditRatingReceived['getCensoredSchema']>> {
        const censoredExample = LoanRequest_ApplicantCreditRatingReceived.example();
        censoredExample.identifier = DEFAULT_CENSOR_MASK;
        if (censoredExample.consorsResponse) censoredExample.consorsResponse.identifier = DEFAULT_CENSOR_MASK;
        // @ts-ignore the example defines this field but is not typed as such
        censoredExample.applicantData.birthPlace = DEFAULT_CENSOR_MASK;
        // @ts-ignore the example defines this field but is not typed as such
        censoredExample.applicantData.firstName = DEFAULT_CENSOR_MASK;
        // @ts-ignore the example defines this field but is not typed as such
        censoredExample.applicantData.lastName = DEFAULT_CENSOR_MASK;
        // @ts-ignore the example defines this field but is not typed as such
        censoredExample.applicantData.currentAddress.street = DEFAULT_CENSOR_MASK;
        // @ts-ignore the example defines this field but is not typed as such
        censoredExample.applicantData.previousAddress.street = DEFAULT_CENSOR_MASK;
        return censoredExample;
    }
}
